import { useEffect, useMemo } from 'react';

import { Accordion, Separator } from '@chakra-ui/react';
import { IQuoteTemplateConfigSchema } from '@monetize/types/app';
import {
  ExperienceConfigurationKeys,
  QuoteTemplateSettingsConfig,
} from '@monetize/types/experience-config';
import { Controller, useForm } from 'react-hook-form';
import { Link as ReactDomLink } from 'react-router-dom';
import { useUpdateExperienceConfiguration } from '../../../api/experienceConfigurationService';
import {
  useConfigQuoteTemplate,
  useGetConfigQuoteTemplate,
} from '../../../api/settingsService';
import {
  MAccordionCustomButton,
  MButton,
  MFlex,
  MFormField,
  MLink,
  MPageContainer,
  MPageLoader,
  MSettingsPageHeader,
  MStack,
  MText,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MSettingAccordionItem } from '../../../components/Monetize/MSettingAccordionItem';
import { QuoteTemplateSettingsList } from '../../../components/Quotes/QuoteTemplates/QuoteTemplateSettingsList';
import { buildQuoteTemplatePermissionsFormValues } from '../../../components/Quotes/QuoteTemplates/quoteTemplateSettings.utils';
import { ROUTES } from '../../../constants';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../constants/settings';
import { useExperienceConfiguration } from '../../../hooks';
import { useQuoteTemplatesList } from '../../../hooks/useQuoteTemplatesList';
import { useDocumentHead } from '../../../services/documentHead';

const QuoteTemplates = () => {
  const { setDocTitle } = useDocumentHead();
  const { quoteTemplates: quoteTemplateOptions } = useQuoteTemplatesList();

  const { data: quoteTemplate, isLoading: isDataLoading } =
    useGetConfigQuoteTemplate({ refetchOnWindowFocus: false });
  const { mutate: updateQuoteTemplateConfig, isPending: isUpdateLoading } =
    useConfigQuoteTemplate({ meta: { showErrorToast: true } });

  const permissionsConfig = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteTemplateSettings,
    { refetchOnWindowFocus: false },
  );
  const { mutate: updatePermissions, isPending: isPermissionsLoading } =
    useUpdateExperienceConfiguration({ meta: { showErrorToast: true } });

  const valuesForm = useForm<IQuoteTemplateConfigSchema>({ mode: 'onChange' });
  const {
    control: valuesControl,
    handleSubmit: handleValuesSubmit,
    formState: { isDirty: isValuesDirty },
    reset: resetValues,
  } = valuesForm;

  const initialPermissions = useMemo(
    () => buildQuoteTemplatePermissionsFormValues(permissionsConfig),
    [permissionsConfig],
  );
  const permissionsForm = useForm<QuoteTemplateSettingsConfig>({
    mode: 'onChange',
    defaultValues: initialPermissions,
  });
  const {
    control: permissionsControl,
    setValue: setPermissionsValue,
    handleSubmit: handlePermissionsSubmit,
    formState: { isDirty: isPermissionsDirty },
    reset: resetPermissions,
  } = permissionsForm;

  useEffect(() => {
    if (quoteTemplate) {
      resetValues({
        ...quoteTemplate,
        // FALLBACK TO DEFAULT TEMPLATE UNTIL BE HAS THIS DATA
        defaultTemplate: quoteTemplate.defaultTemplate ?? 'QUOTE_DEFAULT_1',
      });
    }
  }, [quoteTemplate, resetValues]);

  useEffect(() => {
    resetPermissions(initialPermissions);
  }, [initialPermissions, resetPermissions]);

  useEffect(() => {
    setDocTitle('Settings', 'Quote Templates');
  }, []);

  const isSaving = isUpdateLoading || isPermissionsLoading;
  const isDirty = isValuesDirty || isPermissionsDirty;

  const onSave = () => {
    if (isValuesDirty) {
      handleValuesSubmit(({ showOfferingDescription, ...payload }) => {
        updateQuoteTemplateConfig(payload);
      })();
    }
    if (isPermissionsDirty) {
      handlePermissionsSubmit((configuration) => {
        updatePermissions({
          key: ExperienceConfigurationKeys.quoteTemplateSettings,
          configuration,
        });
      })();
    }
  };

  return (
    <MPageContainer data-testid="settings-quote-templates">
      <MSettingsPageHeader title="Quote Templates">
        <MButton
          type="submit"
          form="customize-quoteTemplate-form"
          data-testid="submit-quoteTemplate-form-top"
          variant="primary"
          isDisabled={isSaving || !isDirty}
          isLoading={isSaving}
        >
          Save
        </MButton>
      </MSettingsPageHeader>
      <MStack w="100%" overflow="auto" p={1} data-testid="quoteTemplates">
        {isDataLoading ? (
          <MPageLoader maxW={SETTINGS_CONTENT_MAX_WIDTH} height="450px" />
        ) : (
          <Accordion.Root
            defaultValue={['0']}
            multiple
            maxW={SETTINGS_CONTENT_MAX_WIDTH}
          >
            <MSettingAccordionItem
              data-testid="qTemplate-accordion"
              description="Configure the default customizations settings for Quotes, and control which roles can view or edit each setting."
              renderAccordionButton={({ isExpanded }) => (
                <MAccordionCustomButton
                  isExpanded={isExpanded}
                  label="Default Customization"
                  data-testid="quote-template-mAccordionBtn"
                />
              )}
            >
              <form
                id="customize-quoteTemplate-form"
                data-testid="customize-quoteTemplate-form"
                onSubmit={(ev) => {
                  ev.preventDefault();
                  onSave();
                }}
              >
                <MFormField label="Quote Template" pt={3}>
                  <Controller
                    name="defaultTemplate"
                    control={valuesControl}
                    render={({ field }) => (
                      <MCustomSelect items={quoteTemplateOptions} {...field} />
                    )}
                  />
                  <MText fontStyle="italic" mt={2}>
                    To override the Quote Template for a specific legal entity,
                    go to the{' '}
                    <MLink fontSize="sm" asChild>
                      <ReactDomLink to={ROUTES.COMPANY_SETTINGS}>
                        Legal Entities
                      </ReactDomLink>
                    </MLink>{' '}
                    page.
                  </MText>
                </MFormField>

                <Separator my={4} />

                <QuoteTemplateSettingsList
                  valuesControl={valuesControl}
                  permissionsControl={permissionsControl}
                  permissionsSetValue={setPermissionsValue}
                  isAmendmentV2Quote={true}
                />

                <MFlex justifyContent="flex-end">
                  <MButton
                    type="submit"
                    data-testid="submit-quoteTemplate-form"
                    variant="primary"
                    mt={8}
                    alignSelf="self-end"
                    isDisabled={isSaving || !isDirty}
                    isLoading={isSaving}
                  >
                    Save
                  </MButton>
                </MFlex>
              </form>
            </MSettingAccordionItem>
          </Accordion.Root>
        )}
      </MStack>
    </MPageContainer>
  );
};

export default QuoteTemplates;
